// SPDX-License-Identifier: Apache-2.0

// useBrainDocs — Solid hook for the brain-agent doc panel.
//
// Port of v1's React `use-brain-docs.ts`. Two responsibilities:
//
//   1. Periodically pull the brain-agent's "what docs are alive right now"
//      summary from the local MCP server (10s polling). The orbit reads this
//      to render claude-session badges, cost/context bars, etc.
//
//   2. Subscribe to the live brain-agent SSE feed so doc nodes can briefly
//      "pulse" when the agent emits a tool_use event. Pulse state is a plain
//      `Map<docId, lastTouchMs>` we expose via an accessor — the orbit reads
//      it on every animation frame and fades the ring down over ~800ms.
//
// Backend reality check (shipped 2026-04-27):
//   - `POST /api/brain/chat`             — exists. SSE stream, but it's the
//                                          USER chat stream, not a global
//                                          fleet event bus. We don't subscribe
//                                          to it from here (a chat without a
//                                          user message returns nothing).
//   - `GET  /api/brain/docs`             — shipped 2026-04-27. Returns
//                                          `{ docs: BrainDocSummary[] }`. v1
//                                          ships an empty registry until the
//                                          supervisor maintains per-doc state;
//                                          the hook still degrades gracefully
//                                          on 404 in case the SPA hits an old
//                                          MCP build.
//   - `GET  /api/brain/events`           — shipped 2026-04-27. SSE stream
//                                          carrying `{ type: 'pulse', docIds,
//                                          pids, tool, ts }` whenever the
//                                          brain-agent supervisor sees a
//                                          tool_use. Live since this build.
//
// On 404 from either endpoint we silently no-op — same behaviour as v1's
// fetcher when the apps/hub server isn't running. Anything 5xx surfaces as
// the `error` signal so the chat panel can show a banner.

import { createSignal, onCleanup, onMount } from "solid-js";
import type { Accessor } from "solid-js";
import { api, isMcpReachable } from "../../lib/api";

// ─── shapes ────────────────────────────────────────────────────────────────

/** Subset of the v1 hub's doc record. We keep this minimal so the hook can
 *  be wired to either the (eventually) ported `/api/brain/docs` route or to
 *  whatever the live MCP supervisor decides to expose. Optional everywhere
 *  except `id`. */
export interface BrainDocSummary {
  id: string;
  title?: string;
  /** ms epoch for "last activity in this doc". */
  updatedAt?: number;
  /** Claude-session metadata when the doc represents a live `claude -p`. */
  pid?: number;
  status?: string;
  cost_usd?: number;
  context_pct?: number;
  /** Catch-all so the hook keeps round-tripping fields the orbit doesn't
   *  yet read but the backend may already emit. */
  [key: string]: unknown;
}

/** A pulse event the SSE feed can dispatch. `docIds` is preferred; v1 used
 *  `pids` because every doc was a live process — we accept both. */
export interface BrainPulseEvent {
  docIds?: string[];
  pids?: number[];
  /** Optional tool name (e.g. "search_docs") — purely metadata for the UI. */
  tool?: string;
  /** Server-assigned ms epoch. */
  ts?: number;
}

export interface UseBrainDocsResult {
  state: Accessor<{
    docs: BrainDocSummary[];
    /** Map<docId, ms-of-last-pulse>. The map IDENTITY is stable; a fresh
     *  state object is emitted whenever a pulse arrives so Solid wakes up,
     *  but the inner `pulses` is the same Map instance — readers can poll
     *  it on every frame without re-reading the accessor. */
    pulses: Map<string, number>;
  }>;
  loading: Accessor<boolean>;
  error: Accessor<string | null>;
  /** Manual refetch, e.g. on chat-panel mount. */
  refresh: () => Promise<void>;
}

// ─── tunables ──────────────────────────────────────────────────────────────

// Tightened from 10s → 30s now that SSE handles live activity. The poll
// is a recovery / resync path only (covers SSE reconnect gaps and
// supervisor restart edges); 30s is plenty for that and keeps the network
// quiet on idle tabs.
const POLL_INTERVAL_MS = 30_000;
/** Pulse rings fade at this rate; we sweep & emit a fresh state at this
 *  cadence so even a single isolated pulse fades cleanly without ever needing
 *  another SSE event. */
const PULSE_GC_MS = 250;
/** Drop pulses older than this. */
const PULSE_TTL_MS = 800;
/** SSE reconnect backoff cap. */
const RECONNECT_CAP_MS = 30_000;

// ─── hook ──────────────────────────────────────────────────────────────────

export function useBrainDocs(): UseBrainDocsResult {
  // The pulses map's IDENTITY is stable; we mutate in place + bump the
  // outer signal to wake Solid up.
  const pulses = new Map<string, number>();

  const [state, setState] = createSignal<{
    docs: BrainDocSummary[];
    pulses: Map<string, number>;
  }>({ docs: [], pulses });
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);

  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let pulseTimer: ReturnType<typeof setInterval> | null = null;
  let sse: EventSource | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectDelay = 1000;
  let stopped = false;

  function emit() {
    setState({ docs: state().docs, pulses });
  }

  function setDocs(docs: BrainDocSummary[]) {
    setState({ docs, pulses });
  }

  // ─── poll /api/brain/docs ────────────────────────────────────────────────

  async function refresh(): Promise<void> {
    if (stopped) return;
    try {
      const res = await api("/api/brain/docs");
      if (res.status === 404) {
        // TODO(backend): endpoint not implemented yet — quietly no-op.
        setDocs([]);
        setError(null);
        return;
      }
      if (!res.ok) {
        setError(`brain/docs: ${res.status}`);
        return;
      }
      const data = (await res.json()) as { docs?: BrainDocSummary[] } | BrainDocSummary[];
      const docs = Array.isArray(data) ? data : (data?.docs ?? []);
      setDocs(docs);
      setError(null);
    } catch (e) {
      // Network failure (server down). v1 surfaces this as `error`; we mirror.
      setError((e as Error).message ?? "fetch failed");
    } finally {
      setLoading(false);
    }
  }

  // ─── SSE pulse subscription ──────────────────────────────────────────────

  function connectSSE() {
    if (stopped) return;
    if (typeof EventSource === "undefined") return; // SSR / non-browser
    // Skip the connect attempt entirely when the MCP backend isn't reachable
    // on this origin — avoids the "EventSource MIME type text/html" console
    // spam on deployed Pages where /api/brain/events 404s into the SPA's
    // index.html.
    if (!isMcpReachable()) return;
    try {
      // EventSource doesn't support custom headers — workspace scoping must
      // be handled server-side via cookie or the global-default workspace.
      // The endpoint may 404 today; the onerror handler below treats that
      // as "not implemented yet" and stops reconnecting.
      sse = new EventSource("/api/brain/events");
    } catch {
      return;
    }

    sse.onopen = () => {
      reconnectDelay = 1000; // reset backoff on a clean open
    };

    sse.onmessage = (ev: MessageEvent) => {
      let parsed: BrainPulseEvent | null = null;
      try {
        parsed = JSON.parse(ev.data) as BrainPulseEvent;
      } catch {
        return;
      }
      if (!parsed) return;
      const now = parsed.ts ?? Date.now();
      let changed = false;
      for (const id of parsed.docIds ?? []) {
        pulses.set(id, now);
        changed = true;
      }
      // v1 fallback — `pids` carry numeric ids; convert to string keys so the
      // map shape stays uniform.
      for (const pid of parsed.pids ?? []) {
        pulses.set(String(pid), now);
        changed = true;
      }
      if (changed) emit();
    };

    sse.onerror = () => {
      // EventSource auto-reconnects in some browsers; we close + manage it
      // ourselves to apply backoff and to handle the 404 case cleanly.
      try {
        sse?.close();
      } catch {
        /* ok */
      }
      sse = null;
      if (stopped) return;
      reconnectTimer = setTimeout(() => {
        reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_CAP_MS);
        connectSSE();
      }, reconnectDelay);
    };
  }

  // ─── lifecycle ──────────────────────────────────────────────────────────

  onMount(() => {
    void refresh();
    pollTimer = setInterval(() => void refresh(), POLL_INTERVAL_MS);

    pulseTimer = setInterval(() => {
      const now = Date.now();
      let changed = false;
      for (const [id, ts] of pulses) {
        if (now - ts > PULSE_TTL_MS) {
          pulses.delete(id);
          changed = true;
        }
      }
      if (changed) emit();
    }, PULSE_GC_MS);

    connectSSE();
  });

  onCleanup(() => {
    stopped = true;
    if (pollTimer) clearInterval(pollTimer);
    if (pulseTimer) clearInterval(pulseTimer);
    if (reconnectTimer) clearTimeout(reconnectTimer);
    try {
      sse?.close();
    } catch {
      /* ok */
    }
    sse = null;
  });

  return { state, loading, error, refresh };
}
